/**
 * /app/atlas — ATLAS programme dashboard (PULSE-247). Admin-only.
 *
 * Read-only view over atlas_*: phase map with live statuses, regime ribbon
 * (v3 core, prod), IC exam map (bar chart, pass/fail), multiplicity ledger,
 * mechanics registry. Data comes from /api/atlas/*; no writes from the UI.
 */
import { useQuery } from '@tanstack/react-query'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'

import { api } from '@/lib/api'
import { cn } from '@/lib/cn'

// ─── data hooks ────────────────────────────────────────────────────────
const useAtlas = <T,>(path: string) =>
  useQuery<T>({
    queryKey: ['atlas', path],
    queryFn: async () => (await api.get(`/atlas/${path}`)).data,
    refetchInterval: 60_000,
  })

type Overview = {
  regime: { ts: string; regime: string; crisis: boolean; confidence: number; since: string } | null
  counts: Record<string, number>
}
type RegimeSeg = { regime: string; crisis: boolean; from: string; to: string; hours: number }
type Exam = { exam: string; feature: string; mean_ic: number; t: number; passed: boolean; lag1?: number }
type Entry = { id: number; ts: string; name?: string; stage?: string; kind: string; summary?: string; final_pass?: boolean | null }
type Live = {
  nav: number | null; nav_ts: string | null; day_anchor: number | null
  day_pnl_pct: number | null; halted: boolean; gross: number
  equity: { ts: string; nav: number }[]
  positions: { symbol: string; w: number; target_w: number | null }[]
  exec_24h: { passive: boolean; filled: boolean; n: number; avg_sf: number | null }[]
  fills: { ts: string; symbol: string; side: string; price: number; delta_w: number; kind: string }[]
}
type Mechanic = {
  id: string; status: string; source_strategy_id: number; direction: string
  expectation?: { mean_pp?: number; live_pp_haircut?: number; n?: number }
  exit?: { tp_pct?: number; sl_pct?: number; hold_min?: number }
  w0_base?: number; mechanism?: string; git_sha?: string
}

// ─── static phase map (programme state as of the current ledger) ───────
const PHASES = [
  { id: 'Ф0', title: 'Режим-ядро', status: 'done', note: 'v3 в проде (Z=0.75/V=1.3); скоуп: CRISIS-защита + бета-модуляция (решение A)' },
  { id: 'Ф0.5', title: 'Данные 2020+', status: 'done', note: '14M klines, 2.07M funding-сеттлов, PIT-вселенная 581k строк' },
  { id: 'Ф1', title: 'Симулятор', status: 'done', note: 'identity 0.0000pp; funding 100%; издержки = константа 6.9bp+fees' },
  { id: 'Ф2', title: 'Полки / факторы', status: 'active', note: 'rev_4h в демо-форварде к пересдаче (сентябрь); directional-обёртка паспортов впереди' },
  { id: 'Ф3', title: 'Демо-карантин', status: 'active', note: 'ДЕМО ТОРГУЕТ с 20.07 (акк ATLAS): полка rev_4h, PostOnly-лестницы, kill −3%' },
  { id: 'Реал', title: 'Пилот 10%', status: 'wait', note: 'только после зелёной Ф3 и отдельного решения' },
] as const

const REGIME_COLORS: Record<string, string> = {
  RISK_ON: '#34d399', RANGE: '#94a3b8', STRESS: '#f87171',
}

export default function Atlas() {
  const { data: ov } = useAtlas<Overview>('overview')
  const { data: reg } = useAtlas<{ segments: RegimeSeg[]; total_hours: number }>('regime?days=180')
  const { data: ex } = useAtlas<{ exams: Exam[] }>('exams')
  const { data: jr } = useAtlas<{ entries: Entry[] }>('journal?limit=100')
  const { data: mech } = useAtlas<{ mechanics: Mechanic[] }>('mechanics')
  const { data: lv } = useAtlas<Live>('live')

  const r = ov?.regime
  const trials = (jr?.entries ?? []).filter((e) => e.kind === 'trial')

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">ATLAS</h1>
          <p className="text-sm text-textDim mt-1">
            Target-position система · PULSE-247 · read-only витрина программы
          </p>
        </div>
        {r && (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ background: REGIME_COLORS[r.regime] ?? '#888' }}
            />
            <div className="text-sm">
              <span className="font-semibold">{r.regime}</span>
              {r.crisis && <span className="ml-2 text-red-400 font-semibold">CRISIS</span>}
              <span className="ml-2 text-textFaint">
                conf {Math.round((r.confidence ?? 0) * 100)}% · с {new Date(r.since).toLocaleDateString('ru')}
              </span>
            </div>
          </div>
        )}
      </header>

      {/* фазы */}
      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        {PHASES.map((p) => (
          <div
            key={p.id}
            className={cn(
              'rounded-xl border p-3',
              p.status === 'done' && 'border-emerald-700/50 bg-emerald-500/5',
              p.status === 'active' && 'border-amber-600/60 bg-amber-500/10',
              (p.status as string) !== 'done' && (p.status as string) !== 'active' && 'border-border bg-surface',
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-textFaint">{p.id}</span>
              <span className={cn(
                'text-[10px] uppercase tracking-wide',
                p.status === 'done' && 'text-emerald-400',
                p.status === 'active' && 'text-amber-400',
                p.status !== 'done' && p.status !== 'active' && 'text-textFaint',
              )}>
                {p.status === 'done' ? 'закрыта' : p.status === 'active' ? 'сейчас' : 'впереди'}
              </span>
            </div>
            <div className="text-sm font-medium mt-1">{p.title}</div>
            <div className="text-[11px] text-textDim mt-1 leading-snug">{p.note}</div>
          </div>
        ))}
      </section>


      {/* Ф3 live-торговля (demo, аккаунт ATLAS) */}
      <section className="rounded-xl border border-border bg-surface p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold">Live-торговля · Ф3 демо-карантин</h2>
          {lv && (
            <span className={cn('text-[11px] px-2 py-0.5 rounded uppercase',
              lv.halted ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400')}>
              {lv.halted ? 'HALT' : 'работает'}
            </span>
          )}
        </div>
        {lv && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="rounded-lg border border-border p-3 text-center">
                <div className="text-lg font-mono font-semibold">
                  {lv.nav != null ? `$${lv.nav.toFixed(2)}` : '—'}
                </div>
                <div className="text-[11px] text-textDim">NAV (demo)</div>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <div className={cn('text-lg font-mono font-semibold',
                  (lv.day_pnl_pct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                  {lv.day_pnl_pct != null ? `${lv.day_pnl_pct > 0 ? '+' : ''}${lv.day_pnl_pct.toFixed(2)}%` : '—'}
                </div>
                <div className="text-[11px] text-textDim">PnL за день (kill −3%)</div>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <div className="text-lg font-mono font-semibold">{lv.positions.length}</div>
                <div className="text-[11px] text-textDim">позиций</div>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <div className="text-lg font-mono font-semibold">{lv.gross.toFixed(2)}</div>
                <div className="text-[11px] text-textDim">гросс (доля NAV)</div>
              </div>
            </div>
            {lv.equity.length > 1 && (
              <div className="h-40 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={lv.equity} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.12} />
                    <XAxis dataKey="ts" tick={{ fontSize: 9, fill: '#94a3b8' }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('ru', { day: '2-digit', month: '2-digit' })}
                      minTickGap={40} />
                    <YAxis domain={['dataMin - 1', 'dataMax + 1']}
                      tick={{ fontSize: 9, fill: '#94a3b8' }} width={52}
                      tickFormatter={(v) => `$${Number(v).toFixed(0)}`} />
                    <Tooltip
                      contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
                      labelFormatter={(v) => new Date(String(v)).toLocaleString('ru')}
                      formatter={(v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : String(v))} />
                    <Area type="monotone" dataKey="nav" stroke="#34d399"
                      fill="#34d399" fillOpacity={0.12} strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div>
                <h3 className="text-xs font-semibold mb-1 text-textDim">
                  Позиции (факт vs target, доля NAV)
                </h3>
                <div className="max-h-56 overflow-y-auto pr-1">
                  <table className="w-full text-xs">
                    <thead className="text-textFaint text-left">
                      <tr><th>символ</th><th className="text-right">факт</th>
                        <th className="text-right">target</th></tr>
                    </thead>
                    <tbody className="font-mono">
                      {lv.positions.map((p) => (
                        <tr key={p.symbol} className="border-t border-border/40">
                          <td>{p.symbol}</td>
                          <td className={cn('text-right', p.w >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                            {(p.w * 100).toFixed(2)}%
                          </td>
                          <td className="text-right text-textDim">
                            {p.target_w != null ? `${(p.target_w * 100).toFixed(2)}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="text-xs font-semibold mb-1 text-textDim">
                  Исполнение за 24ч · последние филы
                </h3>
                <div className="text-[11px] text-textDim mb-2 font-mono">
                  {lv.exec_24h.map((s2, i) => (
                    <div key={i}>
                      {s2.passive ? 'лимитки' : 'маркеты'} {s2.filled ? 'филы' : 'экспайры'}:
                      {' '}{s2.n}{s2.avg_sf != null ? ` · shortfall ${s2.avg_sf > 0 ? '+' : ''}${s2.avg_sf}bp` : ''}
                    </div>
                  ))}
                </div>
                <div className="max-h-40 overflow-y-auto pr-1 space-y-0.5">
                  {lv.fills.map((f, i) => (
                    <div key={i} className="text-[11px] font-mono flex gap-2 border-t border-border/30 pt-0.5">
                      <span className="text-textFaint">
                        {new Date(f.ts).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className={f.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>
                        {f.side}
                      </span>
                      <span>{f.symbol}</span>
                      <span className="text-textDim">@{f.price}</span>
                      <span className="text-textFaint">{f.kind}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* режим-лента */}
      <section className="rounded-xl border border-border bg-surface p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold">Режим-ядро v3 · последние 180 дней</h2>
          <span className="text-xs text-textFaint">
            {reg ? `${reg.total_hours} ч разметки` : '…'}
          </span>
        </div>
        {reg && reg.segments.length > 0 && (
          <>
            <div className="flex h-8 w-full overflow-hidden rounded-md">
              {reg.segments.map((s, i) => (
                <div
                  key={i}
                  title={`${s.regime}${s.crisis ? ' + CRISIS' : ''}\n${new Date(s.from).toLocaleString('ru')} → ${new Date(s.to).toLocaleString('ru')} (${s.hours}ч)`}
                  style={{
                    width: `${(s.hours / reg.total_hours) * 100}%`,
                    background: REGIME_COLORS[s.regime] ?? '#666',
                    opacity: s.crisis ? 1 : 0.75,
                    boxShadow: s.crisis ? 'inset 0 -3px 0 #7f1d1d' : undefined,
                  }}
                />
              ))}
            </div>
            <div className="flex gap-4 mt-2 text-[11px] text-textDim">
              {Object.entries(REGIME_COLORS).map(([k, c]) => (
                <span key={k} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm inline-block" style={{ background: c }} />
                  {k}
                </span>
              ))}
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm inline-block bg-red-900" />
                тёмная кромка = CRISIS-overlay
              </span>
            </div>
          </>
        )}
      </section>

      {/* IC-карта экзаменов */}
      <section className="rounded-xl border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold mb-1">Карта факторов · IC-экзамены (ledger)</h2>
        <p className="text-xs text-textDim mb-3">
          Зелёное = гейты пройдены (pre-reg G1-G3). Пунктир — зачётный порог |IC| 0.02.
        </p>
        {ex && ex.exams.length > 0 && (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ex.exams.map((e) => ({
                  ...e,
                  label: `${e.feature}${e.exam.includes('intraday') ? '' : ' (1d)'}`,
                }))}
                margin={{ top: 8, right: 12, bottom: 40, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                <XAxis
                  dataKey="label" angle={-35} textAnchor="end" interval={0}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <Tooltip
                  contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
                  formatter={(v) => (typeof v === 'number' ? v.toFixed(4) : String(v))}
                />
                <ReferenceLine y={0.02} stroke="#eab308" strokeDasharray="4 4" />
                <ReferenceLine y={-0.02} stroke="#eab308" strokeDasharray="4 4" />
                <ReferenceLine y={0} stroke="#475569" />
                <Bar dataKey="mean_ic" radius={[3, 3, 0, 0]}>
                  {ex.exams.map((e, i) => (
                    <Cell key={i} fill={e.passed ? '#34d399' : '#64748b'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-[11px] text-textFaint mt-2">
          Доказаны: rev_1d (6 лет, t=10.6) и rev_4h (intraday, t=15, lag-1 устойчив). Полки на
          taker-издержках (26bp roundtrip) арифметически не проходят — монетизация через
          maker-исполнение (Ф3 executor).
        </p>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* механики */}
        <section className="rounded-xl border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold mb-3">
            Реестр механик · паспорта из git (верифицированы против прода)
          </h2>
          <div className="space-y-2">
            {(mech?.mechanics ?? []).map((m) => (
              <div key={m.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{m.id}</span>
                  <span className={cn(
                    'text-[10px] uppercase px-1.5 py-0.5 rounded',
                    m.status === 'live' && 'bg-emerald-500/15 text-emerald-400',
                    m.status === 'live-watch' && 'bg-amber-500/15 text-amber-400',
                    m.status !== 'live' && m.status !== 'live-watch' && 'bg-surface2 text-textDim',
                  )}>
                    {m.status}
                  </span>
                </div>
                <div className="text-[11px] text-textDim mt-1 leading-snug">{m.mechanism}</div>
                <div className="text-[11px] text-textFaint mt-1 font-mono">
                  exp {m.expectation?.mean_pp ?? '—'}pp → live {m.expectation?.live_pp_haircut ?? '—'}pp
                  {' · '}tp{m.exit?.tp_pct}/sl{m.exit?.sl_pct}/h{m.exit?.hold_min}м
                  {' · '}w0 {m.w0_base} · страта #{m.source_strategy_id}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ledger */}
        <section className="rounded-xl border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold mb-3">
            Multiplicity ledger · {trials.length} записей (каждая итерация — до прогона)
          </h2>
          <div className="max-h-[28rem] overflow-y-auto pr-1 space-y-1">
            {trials.map((e) => (
              <div key={e.id} className="flex items-start gap-2 text-xs border-b border-border/50 py-1.5">
                <span className="text-textFaint font-mono shrink-0">#{e.id}</span>
                <span className="text-textFaint shrink-0">
                  {new Date(e.ts).toLocaleDateString('ru')}
                </span>
                <span className="font-mono shrink-0">{e.name}</span>
                <span className={cn(
                  'shrink-0 px-1 rounded text-[10px] uppercase',
                  e.stage === 'result' && (e.final_pass ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/10 text-red-400'),
                  e.stage === 'pre-registration' && 'bg-sky-500/10 text-sky-400',
                  e.stage?.includes('amendment') && 'bg-amber-500/10 text-amber-400',
                )}>
                  {e.stage}
                </span>
                {e.summary && (
                  <span className="text-textDim truncate" title={e.summary}>{e.summary}</span>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* данные-фундамент */}
      {ov?.counts && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            ['klines', 'часовых баров 2020+'],
            ['settlements', 'funding-сеттлов'],
            ['universe_rows', 'PIT-universe (eligible)'],
            ['mechanics', 'паспортов механик'],
            ['trials', 'записей ledger'],
          ].map(([k, label]) => (
            <div key={k} className="rounded-xl border border-border bg-surface p-3 text-center">
              <div className="text-lg font-semibold font-mono">
                {(ov.counts[k] ?? 0).toLocaleString('ru')}
              </div>
              <div className="text-[11px] text-textDim">{label}</div>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}
